import { MGPMap, Utils } from '@everyboard/lib';
import { BoardValue } from './AI/BoardValue';
import { Coord } from './Coord';
import { Ordinal } from './Ordinal';
export class AbstractNInARowHelper {
    getOwner;
    N;
    directions;
    doubleDirections;
    constructor(getOwner, N, directions) {
        this.getOwner = getOwner;
        this.N = N;
        this.directions = directions;
        // The aim of this loop is to count down and up as only one "double direction"
        const doubleDirections = [];
        for (const direction of directions) {
            if (doubleDirections.includes(direction) || doubleDirections.includes(direction.getOpposite())) {
                continue;
            }
            else {
                doubleDirections.push(direction);
            }
        }
        this.doubleDirections = new Set(doubleDirections);
    }
    getBoardValue(state) {
        let score = 0;
        for (const coordAndContent of state.getCoordsAndContents()) {
            const piece = coordAndContent.content;
            const coord = coordAndContent.coord;
            if (this.getOwner(piece, state).isPlayer()) {
                const squareScore = this.getSquareScore(state, coord);
                if (BoardValue.isVictoryValue(squareScore)) {
                    return BoardValue.of(squareScore);
                }
                else {
                    score += squareScore;
                }
            }
        }
        return BoardValue.of(score);
    }
    getSquareScore(state, coord) {
        const piece = state.getPieceAt(coord);
        const ally = this.getOwner(piece, state);
        Utils.assert(ally.isPlayer(), 'getSquareScore should not be called with PlayerOrNone.NONE piece');
        const freeSpaceByDirs = new MGPMap();
        const alliesByDirs = new MGPMap();
        for (const dir of this.directions) {
            const freeSpaceAndAllies = this.getNumberOfFreeSpacesAndAllies(state, coord, dir, ally);
            freeSpaceByDirs.set(dir, freeSpaceAndAllies[0]);
            alliesByDirs.set(dir, freeSpaceAndAllies[1]);
        }
        const score = this.getScoreFromDirectionAlliesAndFreeSpaces(alliesByDirs, freeSpaceByDirs);
        return score * ally.getScoreModifier();
    }
    getScoreFromDirectionAlliesAndFreeSpaces(alliesByDirs, freeSpaceByDirs) {
        let score = 0;
        for (const dir of this.doubleDirections) {
            // for each pair of opposite directions
            const directionAllies = alliesByDirs.get(dir).get();
            const oppositeDirectionAllies = alliesByDirs.get(dir.getOpposite()).get();
            const lineAllies = directionAllies + oppositeDirectionAllies;
            if (this.N <= lineAllies + 1) {
                return Number.POSITIVE_INFINITY;
            }
            const directionFreeSpaces = freeSpaceByDirs.get(dir).get();
            const oppositeDirectionFreeSpaces = freeSpaceByDirs.get(dir.getOpposite()).get();
            const lineFreeSpaces = directionFreeSpaces + oppositeDirectionFreeSpaces;
            if (this.N <= lineFreeSpaces + 1) {
                score += 2 + lineFreeSpaces - this.N;
            }
        }
        return score;
    }
    getNumberOfFreeSpacesAndAllies(state, i, dir, ally) {
        /**
         * for a square at the coord i, containing an ally
         * we go through the board from this coord in the direction dir
         * and until a maximal distance of N cases
         */
        let freeSpaces = 0; // the number of aligned free square
        let allies = 0; // the number of alligned allies
        let allAlliesAreSideBySide = true;
        let coord = new Coord(i.x + dir.x, i.y + dir.y);
        let testedCoords = 1;
        const opponent = ally.getOpponent();
        while (state.isOnBoard(coord) && testedCoords < this.N) {
            // while we're on the board
            const currentSpace = state.getPieceAt(coord);
            const currentOwner = this.getOwner(currentSpace, state);
            if (currentOwner === opponent) {
                return [freeSpaces, allies];
            }
            if (currentOwner === ally && allAlliesAreSideBySide) {
                allies++;
            }
            else {
                allAlliesAreSideBySide = false; // we stop counting the allies on this line
            }
            // as soon as there is a hole
            if (currentOwner !== opponent && currentOwner !== ally) {
                freeSpaces++;
            }
            coord = coord.getNext(dir);
            testedCoords++;
        }
        return [freeSpaces, allies];
    }
    getVictoriousCoord(state) {
        const coords = [];
        for (const coordAndContents of state.getCoordsAndContents()) {
            if (this.getOwner(coordAndContents.content, state).isPlayer()) {
                const coord = coordAndContents.coord;
                const squareScore = this.getSquareScore(state, coord);
                if (BoardValue.isVictoryValue(squareScore)) {
                    coords.push(coord);
                }
            }
        }
        return coords;
    }
}
export class NInARowHelper extends AbstractNInARowHelper {
    constructor(getOwner, N) {
        super(getOwner, N, Ordinal.ORDINALS);
    }
}
